import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGlobe } from 'react-icons/fa';

const Header = () => {
    const { t, i18n } = useTranslation();
    const [activeSection, setActiveSection] = useState('about');
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Detectar scroll para cambiar el estilo del header
    useEffect(() => {
        const handleScroll = () => {
            const sections = ['about', 'services', 'projects', 'skills', 'contact'];

            // Detectar la sección activa basada en la posición de scroll
            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom >= 100) {
                        setActiveSection(section);
                        break;
                    }
                }
            }

            // Cambiar estilo del header al hacer scroll
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const menuItems = [
        { id: 'about', text: t('header.about') },
        { id: 'services', text: t('header.services') },
        { id: 'projects', text: t('header.projects') },
        { id: 'skills', text: t('header.skills') },
        { id: 'contact', text: t('header.contact') }
    ];

    const handleNavClick = (id) => {
        setActiveSection(id);
        setMobileMenuOpen(false);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    // Detectar si es móvil
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
            if (window.innerWidth >= 992) {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <motion.header
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            style={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: scrolled
                    ? 'rgba(0, 0, 0, 0.95)'
                    : '#000000',
                backdropFilter: scrolled ? 'blur(10px)' : 'none',
                boxShadow: scrolled
                    ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                    : 'none',
                transition: 'all 0.3s ease',
                borderBottom: scrolled ? '1px solid rgba(204, 0, 51, 0.3)' : '1px solid transparent'
            }}
        >
            <nav className="container" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: scrolled ? '15px 20px' : '20px 20px',
                transition: 'padding 0.3s ease'
            }}>
                {/* Logo/Brand */}
                <motion.a
                    href="#"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    style={{
                        fontSize: scrolled ? '1.4rem' : '1.6rem',
                        fontWeight: '800',
                        color: 'white',
                        textDecoration: 'none',
                        letterSpacing: '0.5px',
                        fontFamily: 'Montserrat, sans-serif',
                        position: 'relative',
                        transition: 'font-size 0.3s ease'
                    }}
                >
                    <span style={{ color: '#cc0033' }}>yalex</span>
                    <span style={{ color: 'white' }}>mtz</span>
                </motion.a>

                {/* Desktop Menu */}
                {!isMobile && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                        <ul style={{
                            display: 'flex',
                            listStyle: 'none',
                            margin: 0,
                            padding: 0,
                            gap: '5px',
                            alignItems: 'center'
                        }}>
                            {menuItems.map((item) => (
                                <motion.li
                                    key={item.id}
                                    whileHover={{ y: -2 }}
                                    transition={{ type: 'spring', stiffness: 300 }}
                                >
                                    <a
                                        href={`#${item.id}`}
                                        onClick={() => handleNavClick(item.id)}
                                        style={{
                                            color: activeSection === item.id ? '#cc0033' : 'white',
                                            textDecoration: 'none',
                                            padding: '10px 18px',
                                            borderRadius: '6px',
                                            fontSize: '0.95rem',
                                            fontWeight: activeSection === item.id ? '600' : '500',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            display: 'block',
                                            background: activeSection === item.id
                                                ? 'rgba(204, 0, 51, 0.1)'
                                                : 'transparent'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (activeSection !== item.id) {
                                                e.target.style.color = '#cc0033';
                                                e.target.style.background = 'rgba(204, 0, 51, 0.05)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (activeSection !== item.id) {
                                                e.target.style.color = 'white';
                                                e.target.style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        {item.text}
                                        {activeSection === item.id && (
                                            <motion.span
                                                layoutId="activeIndicator"
                                                style={{
                                                    position: 'absolute',
                                                    bottom: '6px',
                                                    left: '18px',
                                                    right: '18px',
                                                    height: '2px',
                                                    background: '#cc0033',
                                                    borderRadius: '2px'
                                                }}
                                                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                            />
                                        )}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>

                        {/* Language Selector */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <FaGlobe style={{ color: '#cc0033', fontSize: '1rem' }} />
                            <button
                                onClick={() => changeLanguage('es')}
                                style={{
                                    background: i18n.language === 'es' ? '#cc0033' : 'transparent',
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                ES
                            </button>
                            <button
                                onClick={() => changeLanguage('en')}
                                style={{
                                    background: i18n.language === 'en' ? '#cc0033' : 'transparent',
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                EN
                            </button>
                        </div>
                    </div>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            background: 'transparent',
                            border: '2px solid #cc0033',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            transition: 'all 0.3s ease'
                        }}
                        aria-label="Toggle menu"
                    >
                        <span style={{
                            width: '24px',
                            height: '2px',
                            background: mobileMenuOpen ? '#cc0033' : 'white',
                            transition: 'all 0.3s ease',
                            transform: mobileMenuOpen ? 'rotate(45deg) translateY(6px)' : 'none'
                        }} />
                        <span style={{
                            width: '24px',
                            height: '2px',
                            background: mobileMenuOpen ? '#cc0033' : 'white',
                            transition: 'all 0.3s ease',
                            opacity: mobileMenuOpen ? 0 : 1
                        }} />
                        <span style={{
                            width: '24px',
                            height: '2px',
                            background: mobileMenuOpen ? '#cc0033' : 'white',
                            transition: 'all 0.3s ease',
                            transform: mobileMenuOpen ? 'rotate(-45deg) translateY(-6px)' : 'none'
                        }} />
                    </button>
                )}
            </nav>

            {/* Mobile Menu */}
            {isMobile && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                        height: mobileMenuOpen ? 'auto' : 0,
                        opacity: mobileMenuOpen ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                        overflow: 'hidden',
                        background: 'rgba(0, 0, 0, 0.98)',
                        borderTop: mobileMenuOpen ? '1px solid rgba(204, 0, 51, 0.3)' : 'none',
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        width: '100%',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                    }}
                >
                    <ul style={{
                        listStyle: 'none',
                        margin: 0,
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        {menuItems.map((item, index) => (
                            <motion.li
                                key={item.id}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{
                                    x: mobileMenuOpen ? 0 : -20,
                                    opacity: mobileMenuOpen ? 1 : 0
                                }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <a
                                    href={`#${item.id}`}
                                    onClick={() => handleNavClick(item.id)}
                                    style={{
                                        color: activeSection === item.id ? '#cc0033' : 'white',
                                        textDecoration: 'none',
                                        padding: '12px 16px',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        fontWeight: activeSection === item.id ? '600' : '500',
                                        display: 'block',
                                        background: activeSection === item.id
                                            ? 'rgba(204, 0, 51, 0.15)'
                                            : 'transparent',
                                        transition: 'all 0.3s ease',
                                        borderLeft: activeSection === item.id
                                            ? '3px solid #cc0033'
                                            : '3px solid transparent'
                                    }}
                                >
                                    {item.text}
                                </a>
                            </motion.li>
                        ))}

                        {/* Mobile Language Selector */}
                        <li style={{
                            marginTop: '10px',
                            paddingTop: '10px',
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 16px'
                            }}>
                                <FaGlobe style={{ color: '#cc0033', fontSize: '1.2rem' }} />
                                <button
                                    onClick={() => changeLanguage('es')}
                                    style={{
                                        background: i18n.language === 'es' ? '#cc0033' : 'rgba(255, 255, 255, 0.1)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        flex: 1,
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Español
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    style={{
                                        background: i18n.language === 'en' ? '#cc0033' : 'rgba(255, 255, 255, 0.1)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        flex: 1,
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    English
                                </button>
                            </div>
                        </li>
                    </ul>
                </motion.div>
            )}
        </motion.header>
    );
};

export default Header;